<template>
  <BlBackdrop
    title="Edit prompts"
    label="Update"
    :submitLoading="submitLoading"
    :disabled="isDisabledSubmit"
    @backdrop-close="onCancel"
    @backdrop-action="onSubmit"
  >
    <div
      class="flex flex-col gap-4 px-8 overflow-y-auto"
      :style="{ width: '48rem', maxHeight: 'calc(100vh - 16rem)' }"
    >
      <div class="flex w-full gap-2 px-2 py-2 rounded bg-cornflower-200">
        <BlIcon icon="toast-info" color="cornflower-8" class="w-4 h-4 mt-1" />
        <div>
          <BlTextBody2
            text="Editing the score for prompts will update the calculation of all existing records that use them."
            class="text-cornflower-800"
          />
        </div>
      </div>
      <div
        class="flex items-center justify-center p-4 border rounded bg-grey-100 h-76 border-grey-200"
      >
        <PromptingPreview
          :prompts="defaultPrompts"
          :style="{ transform: 'scale(0.5 , 0.5)' }"
        />
      </div>
      <div class="mx-6">
        <div class="flex items-start justify-start w-full mb-2 -ml-2">
          <div class="flex-shrink-0 w-2" />
          <BlTextSubtitle4
            text="Score (%)"
            class="flex-shrink-0 w-18"
            :style="{ color: '#667085' }"
          />
          <BlTextSubtitle4
            text="Code"
            class="flex-shrink-0 w-18"
            :style="{ color: '#667085' }"
          />
          <BlTextSubtitle4
            text="Prompt name"
            class="w-full"
            :style="{ color: '#667085' }"
          />
          <div class="flex-shrink-0 w-6" />
        </div>
        <div class="flex flex-col gap-4">
          <div
            v-for="prompt in defaultPrompts"
            :key="prompt.id + '-' + prompt.position"
            :id="prompt.id"
            class="flex items-center gap-4"
          >
            <PromptingItem
              :prompt="prompt"
              @update="onUpdatePrompt"
              @delete="onRemovePrompt"
              :directDelete="true"
              :disabledDelete="prompt.is_used"
              :tooltipDelete="
                prompt.is_used
                  ? 'You cannot delete this prompt because it has been used in the session.'
                  : ''
              "
              class=""
            />
          </div>
        </div>
        <div class="flex mt-4">
          <BlInputButton
            icon="plus"
            :label="'Add prompt'"
            @clicked="onAddPrompt"
            type="plain"
            class="px-0"
          />
        </div>
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions } from "vuex";
import draggable from "vuedraggable";

import PromptingItem from "../../../repository/sbt/PromptingItem.vue";
import PromptingPreview from "../../../repository/sbt/PromptingPreview.vue";
import BlBackdrop from "../../../BlBackdrop.vue";
import BlTextSubtitle4 from "../../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlIcon from "../../../BlIcon.vue";

export default {
  name: "EditPromptsSbtModal",
  components: {
    draggable,
    PromptingItem,
    PromptingPreview,
    //
    BlBackdrop,
    BlTextSubtitle4,
    BlTextBody2,
    BlInputButton,
    BlIcon
  },
  props: ["targetId", "targetName", "prompts"],
  data() {
    return {
      submitLoading: false,
      defaultPrompts: [],
      destroyPrompts: []
    };
  },
  computed: {
    isDisabledSubmit() {
      let isScoreInvalid = false;
      this.defaultPrompts.forEach(i => {
        if (i.score === null || i.score < 0 || i.score > 100) {
          isScoreInvalid = true;
        }
      });
      if (isScoreInvalid) return true;
      return false;
    }
  },
  watch: {
    prompts(val) {
      this.generateDefaultPrompts(val);
    }
  },
  mounted() {
    this.generateDefaultPrompts(this.prompts);
  },
  methods: {
    ...mapActions(["getTarget", "updateTarget", "showToast"]),
    generateDefaultPrompts(val = []) {
      if (val && val.length) {
        this.defaultPrompts = [...val];
        this.sortPrompts();
        this.destroyPrompts = [];
      }
    },
    onAddPrompt() {
      function getRandomId() {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let combination = "";
        for (let i = 0; i < 6; i++) {
          combination += chars[Math.round(Math.random() * 35)];
        }
        return `new-prompt-${combination}`;
      }

      const prompt = {
        id: getRandomId(),
        score: 0,
        abbreviation: "NP",
        position: 0,
        name: "New Prompt",
        is_default: false
      };

      const zeroScorePromptIndex = this.defaultPrompts.findIndex(
        p => p.score === 0
      );

      if (zeroScorePromptIndex !== -1) {
        this.defaultPrompts.splice(zeroScorePromptIndex, 0, prompt);
      } else {
        this.defaultPrompts.push(prompt);
      }

      this.sortPrompts();
    },
    onUpdatePrompt(item) {
      const index = this.defaultPrompts.findIndex(i => i.id === item.id);
      if (index > -1) {
        this.defaultPrompts[index] = item;
      }
      this.sortPrompts();
    },
    sortPrompts() {
      this.defaultPrompts = this.defaultPrompts
        .slice()
        .sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          return a.position - b.position;
        })
        .map((prompt, index) => ({
          ...prompt,
          position: index + 1
        }));
    },
    onRemovePrompt(item) {
      if (!item.id.toString().includes("new")) {
        this.destroyPrompts.push({ id: item.id, _destroy: 1 });
      }

      const arr = [...this.defaultPrompts].filter(i => i.id !== item.id);
      this.defaultPrompts = arr.map((i, n) => ({ ...i, position: n + 1 }));
    },
    async onSubmit() {
      const prompts = this.defaultPrompts
        .sort((a, b) => a.position - b.position)
        .map((i, n) => {
          const useId = !i.id.toString().includes("new");
          return {
            ...(useId ? { id: i.id } : {}),
            name: i.name,
            abbreviation: i.abbreviation,
            score: i.score,
            color: "lake",
            position: i.position
          };
        });

      const params = {
        id: this.targetId,
        target: {
          name: this.targetName,
          type: "Target::Sbt",
          prompts_attributes: [...prompts, ...this.destroyPrompts]
        }
      };

      this.submitLoading = true;
      const { success } = await this.updateTarget(params);
      this.submitLoading = false;
      if (!success) return;

      const title = "Prompts updated";
      const message = "Success! The prompts has been updated.";
      this.showToast({ title, message, type: "success" });
      this.$emit("close-modal");
    },
    async onCancel() {
      const params = { id: this.targetId };

      this.submitLoading = true;
      const { success } = await this.getTarget(params);
      this.submitLoading = false;
      if (!success) return;
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/modal/EditPromptsSbtModal.vue